import { useState } from 'react';
import { Body1, Button, Subtitle2, Tooltip } from '@fluentui/react-components';
import { DeleteRegular } from '@fluentui/react-icons';
import ItemsTable from '@modules/items/components/ItemsTable/ItemsTable';
import type { TItem } from '@modules/items/types/items.types';
import { ConfirmDialog, type TConfirmDialogState } from '@shared/components/dialogs/ConfirmDialog';
import { TableActionButtons } from '@shared/components/tables/TableActionButtons';
import { TASK_LINKED_ITEMS_PREVIEW_COUNT } from '../constants';
import { getLinkedItemKey, getUnavailableLabel } from '../linkedItems.helper';

type TProps = {
  linkedItems: TItem[];
  /** Adds a bin to every row, including items the user cannot open. */
  onRemove?: (item: TItem) => void;
};

const TaskLinkedItemsList = ({ linkedItems, onRemove }: TProps) => {
  const [showAll, setShowAll] = useState(false);
  const [removeDialogState, setRemoveDialogState] = useState<TConfirmDialogState>();
  const confirmRemove = (item: TItem) => {
    const dialogState: TConfirmDialogState = {
      isOpen: true,
      title: 'Remove linked item?',
      content: `"${item.name}" will be removed from the linked items. The item itself does not change.`,
      buttonLabel: 'Remove',
      onConfirm: () => {
        onRemove?.(item);
        setRemoveDialogState({ ...dialogState, isOpen: false });
      },
    };
    setRemoveDialogState(dialogState);
  };
  const hiddenCount = linkedItems.length - TASK_LINKED_ITEMS_PREVIEW_COUNT;
  const rows = (showAll ? linkedItems : linkedItems.slice(0, TASK_LINKED_ITEMS_PREVIEW_COUNT)).map((item) => ({
    key: getLinkedItemKey(item),
    item,
    unavailableLabel: getUnavailableLabel(item),
    actionButton: onRemove && (
      <TableActionButtons>
        <Tooltip content="Remove linked item" relationship="label" withArrow>
          <Button icon={<DeleteRegular />} appearance="transparent" onClick={() => confirmRemove(item)} />
        </Tooltip>
      </TableActionButtons>
    ),
  }));
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <Subtitle2>Linked items{linkedItems.length ? ` (${linkedItems.length})` : ''}</Subtitle2>
      {linkedItems.length ? (
        // A new tab keeps the task, and an unsaved form, open.
        <ItemsTable rows={rows} show={{ date: false, version: true }} openMode="newTab" appearance="plain" />
      ) : (
        <Body1>No linked items.</Body1>
      )}
      {hiddenCount > 0 && (
        <Button className="self-start" appearance="transparent" size="small" onClick={() => setShowAll(!showAll)}>
          {showAll ? 'Show less' : `Show ${hiddenCount} more`}
        </Button>
      )}
      <ConfirmDialog state={removeDialogState} setState={setRemoveDialogState} />
    </div>
  );
};
export default TaskLinkedItemsList;
